import { cn } from '@/lib/utils';
import { useCashFlowEntryDocumentHtml } from '@/hooks/useCashFlow';
import type { CashFlowRequestOptions } from '@/lib/cashFlow';
import { documentPaneState, type CashFlowEntryDocument } from '@/lib/cashFlowDetail';

/**
 * RIGHT pane of the enriched-entry detail (module 1.3): the source document rendered
 * inline. The resolution (none / pending / pdf / html-live, sandbox flag, URLs) lives
 * in `documentPaneState` (lib, unit-tested); this component renders the resolved state
 * and — for an Erply invoice — owns the LIVE printout fetch's OWN loading/error state.
 *
 * v1 parity (architect-approved): the overlay + LEFT facts pane open INSTANTLY from the
 * already-loaded detail, while this RIGHT pane LIVE-FETCHES the RAW vendor printout HTML
 * on open (`useCashFlowEntryDocumentHtml`) with its own independent spinner — the bytes
 * are never stored/mirrored, so the invoice is ALWAYS fresh. The raw HTML renders VERBATIM
 * via `srcDoc` into a SCRIPT-SANDBOXED, opaque-origin iframe (`sandbox=""`, no
 * `allow-scripts`/`allow-same-origin`) so untrusted markup can never run scripts nor reach
 * our origin. `srcDoc` (not `src`) is required because the vendor CSP `frame-ancestors`
 * forbids inline framing the original. "Ava originaalis" opens the VENDOR original
 * (`externalUrl`) in a new tab — a top-level nav the vendor CSP does not block — and stays
 * available while the printout is loading OR if the live fetch fails.
 *
 * Security (architect flag): the SmartAccounts PDF renders from a short-lived signed URL
 * into our PRIVATE bucket (`state.url`), a trusted binary in a plain (non-sandboxed) iframe.
 */
export interface DocumentPaneProps {
  document: CashFlowEntryDocument | null;
  /** The selected entry id — the live printout fetch keys on it (the API reads the
   *  entry's vendor URL server-side, so the web only needs the id). */
  entryId: string;
  /** Shared request options (fetch impl / base URL / bearer) threaded from the drill so
   *  the live fetch reads through the SAME layer as every other call (L14). */
  requestOptions?: CashFlowRequestOptions;
}

export function DocumentPane({ document, entryId, requestOptions }: DocumentPaneProps) {
  const state = documentPaneState(document);
  // Called UNCONDITIONALLY (hooks rule), but only fetches for an Erply `html-live` doc.
  // Every open re-fetches (the hook forces it) so the printout is never stale.
  const printout = useCashFlowEntryDocumentHtml(entryId, {
    ...requestOptions,
    enabled: state.mode === 'html-live',
  });

  if (state.mode === 'none') {
    return (
      <div className="flex flex-1 flex-col bg-[#0c1220]">
        <div className="m-auto flex max-w-[22rem] flex-col items-center gap-2 px-6 text-center">
          <span aria-hidden className="text-2xl text-muted-foreground/40">
            ▤
          </span>
          <p className="text-[13px] font-medium text-muted-foreground">Dokumenti pole veel seotud</p>
          <p className="text-[11px] text-muted-foreground/60">
            Summad tulevad finance-core'ist (L9) ka ilma allikadokumendita.
          </p>
        </div>
      </div>
    );
  }

  const isPending = state.mode === 'pending';
  // "Ava originaalis" target: the VENDOR original for the Erply invoice (live-fetch mode,
  // and openable while it loads / if it fails), OUR signed URL for the SmartAccounts PDF.
  // Null → nothing to open yet (a pending PDF), so the link is hidden. For the live
  // Erply printout the API returns the FRESHLY-RESOLVED vendor link alongside the bytes
  // (Erply rotates the stored link's checksum, so the stored pointer 404s within hours);
  // prefer it, falling back to the stored link only while loading / on failure.
  const openUrl = state.mode === 'pdf' ? state.url : (printout.data?.externalUrl ?? state.externalUrl);

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#0c1220]">
      <div className="flex shrink-0 items-center gap-2 border-b border-border bg-background/60 px-4 py-2.5">
        <span aria-hidden className="text-[hsl(var(--primary))]">
          ▤
        </span>
        <span className="text-[12px] font-semibold text-foreground">{state.label}</span>
        <span
          className={cn(
            'rounded border px-1.5 py-0.5 text-[9px] font-semibold tracking-wide',
            state.sandboxed
              ? 'border-[hsl(var(--income))]/40 bg-[hsl(var(--income))]/12 text-[hsl(var(--income))]'
              : 'border-[hsl(var(--cost))]/40 bg-[hsl(var(--cost))]/12 text-[hsl(var(--cost))]',
          )}
        >
          {state.badge}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70">
          <span aria-hidden>⛨</span>
          {state.sandboxed
            ? 'turvaliselt renderdatud · skriptid keelatud'
            : 'privaatne Storage · allkirjastatud URL'}
        </span>
        {/* "Ava originaalis" opens the ORIGINAL in a new tab: the vendor printout for the
            Erply invoice (works while the live fetch is in flight OR failed — a top-level
            nav is not blocked by the vendor's frame CSP), or our signed URL for the PDF.
            "Laadi alla" (download attribute) is PDF-only — a cross-origin page cannot be
            force-downloaded, and the Erply printout is a page, not a file. */}
        {openUrl !== null && (
          <div className="ml-auto flex items-center gap-1.5">
            <a
              href={openUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-accent"
            >
              <span aria-hidden>↗</span> Ava originaalis
            </a>
            {/* "Ava Erplys" opens the ORIGINAL record in the Erply back-office editor
                (Erply-only; the URL is built server-side from the account client code,
                so it is null — button hidden — for non-Erply or unconfigured docs). */}
            {state.mode === 'html-live' && state.backofficeUrl !== null && (
              <a
                href={state.backofficeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-accent"
              >
                <span aria-hidden>↗</span> Ava Erplys
              </a>
            )}
            {state.mode === 'pdf' && (
              <a
                href={state.url}
                download
                className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-accent"
              >
                <span aria-hidden>↓</span> Laadi alla
              </a>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isPending ? (
          <div className="m-auto flex max-w-[22rem] flex-col items-center gap-2 px-6 text-center">
            <span aria-hidden className="text-2xl text-muted-foreground/40">
              ▤
            </span>
            <p className="text-[13px] font-medium text-muted-foreground">Dokument on seotud, kuid veel peegeldamata</p>
            <p className="text-[11px] text-muted-foreground/60">
              Koopia valmib järgmisel sünkroonimisel. Ava originaal uuel kaardil või proovi hiljem uuesti.
            </p>
          </div>
        ) : state.mode === 'html-live' ? (
          // Erply invoice: the RAW vendor printout is LIVE-FETCHED on open (v1 parity —
          // never a stored mirror, so it is always fresh) with THIS pane's own independent
          // indicator. While it loads → a spinner; on success → the bytes framed VERBATIM
          // via `srcDoc` (no scaling, no fixed width, no injected CSS) in a SCRIPT-SANDBOXED
          // (`sandbox=""`) iframe that fills its flex host (`flex-1 min-h-0`) and SCROLLS
          // WITHIN ITSELF; on failure → an error note (the vendor original stays openable
          // via "Ava originaalis" in the header). `srcDoc` (not `src`) because the vendor
          // CSP forbids framing the original and Storage would serve text/html as text/plain.
          printout.isLoading ? (
            <div className="m-auto flex max-w-[22rem] flex-col items-center gap-2 px-6 text-center">
              <span aria-hidden className="animate-pulse text-2xl text-muted-foreground/40">
                ▤
              </span>
              <p className="text-[13px] font-medium text-muted-foreground">Laadin dokumenti…</p>
              <p className="text-[11px] text-muted-foreground/60">Toon Erply müügiarve otse allikast.</p>
            </div>
          ) : printout.data !== undefined ? (
            <div className="relative min-h-0 flex-1 bg-white">
              <iframe
                title={state.label}
                srcDoc={printout.data.html}
                sandbox=""
                className="h-full w-full border-0 bg-white"
              />
            </div>
          ) : (
            <div className="m-auto flex max-w-[22rem] flex-col items-center gap-2 px-6 text-center">
              <span aria-hidden className="text-2xl text-[hsl(var(--cost))]/60">
                ⚠
              </span>
              <p className="text-[13px] font-medium text-muted-foreground">Dokumendi laadimine ebaõnnestus</p>
              <p className="text-[11px] text-muted-foreground/60">
                Ava originaal uuel kaardil ("Ava originaalis") või proovi hiljem uuesti.
              </p>
            </div>
          )
        ) : (
          // Trusted SmartAccounts PDF: rendered from our signed private-bucket URL.
          <iframe title={state.label} src={state.url} className="h-full w-full border-0 bg-white" />
        )}
      </div>
    </div>
  );
}
